import { Agency } from '../../types/agency'
import * as CAT from '../voohCampaign/types'
import {
  VoohCampaignLocationsFilePublicResponse,
  VoohCampaignLocationsFileResponse,
} from '../voohCampaign/types'
import { client } from '../../components/ApolloProvider'
import {
  VoohCampaignForm,
  VoohMedia,
  VoohMediaFormat,
  VoohMediaTemplate,
  VoohSourceMediaFormat,
  VoohUsedMedia,
} from '../../pages/VoohCampaign/VoohCampaignForm/types'
import { CREATE_VOOH_CAMPAIGN } from './createVoohCampaign'
import { UPDATE_VOOH_CAMPAIGN } from './updateCampaignVooh'
import { UPDATE_VOOH_CAMPAIGN_DETAILS } from './updateVoohCampaignDetails'
import { UPDATE_VOOH_CAMPAIGN_MEDIA } from './updateVoohCampaignMedia'
import { MutationStateResponse, PaginationProps } from '../../types/various'
import { VoohCampaignsFilters } from '../../pages/VoohCampaign/VoohCampaignTableFilters'
import { GET_VOOH_CAMPAIGNS_FOR_TABLE } from './getVoohCampaigns'
import { VoohCampaign } from '../../types/campaign'
import { FetchResult } from '@apollo/client'
import { DESTROY_VOOH_CAMPAIGN } from './destroyVoohCampaign'
import { PUBLISH_VOOH_CAMPAIGN } from './publishVoohCampaign'
import { GET_VOOH_CAMPAIGN, GET_VOOH_CAMPAIGN_PUBLIC } from './getVoohCampaign'
import { GET_VOOH_CAMPAIGN_HISTORY } from './getVoohCampaignHistory'
import { GET_VOOH_BRANDS_NETWORK } from './getVoohBrandsNetwork'
import { VoohImpressionsFilterItem } from '../../pages/VoohCampaign/VoohCampaignDetails/VoohCampaignRealization/types'
import { GET_VOOH_IMPRESSIONS, GET_VOOH_IMPRESSIONS_PUBLIC } from './getVoohImpressions'
import { COPY_VOOH_CAMPAIGN } from './CopyVoohCampaign'
import { CREATE_VOOH_MEDIA_TEMPLATE } from './createVoohMediaTemplate'
import { GET_VOOH_MEDIA_TEMPLATES } from './getVoohMediaTemplates'
import { REMOVE_VOOH_MEDIA_TEMPLATE } from './removeVoohMediaTemplate'
import { CREATE_VOOH_CAMPAIGN_SUMMARY_PDF } from './createVoohCampaignSummaryPdf'
import { FetchPdfTaskStatusResponse } from '../campaign/types'
import { GET_VOOH_CAMPAIGN_POTENTIAL } from './getVoohCampaignPotential'
import { STOP_VOOH_CAMPAIGN } from './stopVoohCampaign'
import { GET_VOOH_CAMPAIGN_LOCATION_FILE } from './getVoohCampaignLocationsFile'
import { GET_VOOH_CAMPAIGN_LOCATION_FILE_PUBLIC } from './getVoohCampaignLocationsFilePublic'
import { GET_VOOH_CAMPAIGN_EMISSIONS } from './getVoohCampaignEmissions'

const voohCampaignApi = {
  createVoohCampaign: (v: {
    agencyId: Agency['id'] | null
    name: VoohCampaignForm['baseValues']['name']
    briefName: VoohCampaignForm['baseValues']['briefName']
    startDate: VoohCampaignForm['baseValues']['startDate'] | null
    endDate: VoohCampaignForm['baseValues']['endDate'] | null
    sourceMediaFormat: VoohSourceMediaFormat
    brandId?: string
    networkId?: string
  }): Promise<CAT.CreateVoohCampaignFR> =>
    client.mutate({
      mutation: CREATE_VOOH_CAMPAIGN,
      variables: {
        agencyId: v.agencyId || null,
        name: v.name,
        briefName: v.briefName,
        startDate: v.startDate || null,
        endDate: v.endDate || null,
        sourceMedia: v.sourceMediaFormat,
        brandId: v.brandId || null,
        networkId: v.networkId || null,
      },
    }),

  updateVoohCampaign: (v: {
    id: VoohCampaignForm['id']
    agencyId: Agency['id'] | null
    advertiserId: string | null
    name: VoohCampaignForm['baseValues']['name']
    briefName: VoohCampaignForm['baseValues']['briefName']
    startDate: VoohCampaignForm['baseValues']['startDate']
    endDate: VoohCampaignForm['baseValues']['endDate']
    supervisors: string[]
    sourceMedia: VoohSourceMediaFormat
    brandId?: string
    networkId?: string
  }): Promise<CAT.UpdateVoohCampaignFR> =>
    client.mutate({
      mutation: UPDATE_VOOH_CAMPAIGN,
      variables: {
        id: v.id,
        agencyId: v.agencyId || null,
        advertiserId: v.advertiserId || null,
        name: v.name,
        briefName: v.briefName,
        startDate: v.startDate || null,
        endDate: v.endDate || null,
        supervisors: v.supervisors,
        sourceMedia: v.sourceMedia,
        brandId: v.brandId || null,
        networkId: v.networkId || null,
      },
    }),

  updateVoohCampaignMedia: (v: {
    campaignId: VoohCampaignForm['id']
    media: VoohUsedMedia[]
  }): Promise<CAT.UpdateVoohCampaignMediaFR> =>
    client.mutate({
      mutation: UPDATE_VOOH_CAMPAIGN_MEDIA,
      variables: {
        campaignId: v.campaignId,
        media: v.media,
      },
    }),

  updateVoohCampaignDetails: (v: {
    id: VoohCampaignForm['id']
    mediaFormat: VoohCampaignForm['detailsValues']['mediaFormat']
    hourFrequency: VoohCampaignForm['detailsValues']['hourFrequency']
    offer: VoohCampaignForm['detailsValues']['offer']
    organizationAttribute: VoohCampaignForm['detailsValues']['organizationAttribute']
    mediaDuration: VoohCampaignForm['detailsValues']['mediaDuration']
  }): Promise<CAT.UpdateVoohCampaignDetailsFR> =>
    client.mutate({
      mutation: UPDATE_VOOH_CAMPAIGN_DETAILS,
      variables: {
        id: v.id,
        mediaFormat: v.mediaFormat,
        hourFrequency: v.hourFrequency,
        offer: v.offer,
        organizationAttribute: v.organizationAttribute,
        mediaDuration: v.mediaDuration,
      },
    }),

  getVoohCampaignsForTable: (
    variables: PaginationProps & {
      filters: Omit<VoohCampaignsFilters, 'brandOrNetworkSelected'> & {
        brandOrNetworkSelected?: boolean
      }
    }
  ): Promise<CAT.GetVoohCampaignsForTableAQR> => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo, text, ...filters } =
      variables.filters

    return client.query({
      query: GET_VOOH_CAMPAIGNS_FOR_TABLE,
      variables: {
        ...variables,
        filters: {
          ...filters,
          startDateFrom: startDateFrom || null,
          startDateTo: startDateTo || null,
          endDateFrom: endDateFrom || null,
          endDateTo: endDateTo || null,
          text: text || null,
        },
      },
    })
  },

  getVoohCampaignHistory: (
    voohCampaignId: VoohCampaign['id']
  ): Promise<CAT.GetCampaignHistoryQueryData> =>
    client.query({
      query: GET_VOOH_CAMPAIGN_HISTORY,
      variables: { voohCampaignId },
    }),

  getVoohCampaignPotential: (
    voohCampaignId: VoohCampaign['id']
  ): Promise<CAT.GetVoohCampaignPotentialQueryData> =>
    client.query({
      query: GET_VOOH_CAMPAIGN_POTENTIAL,
      variables: { voohCampaignId },
    }),

  getVoohImpressions: (
    campaignId: VoohCampaign['id'],
    agglomerations: VoohImpressionsFilterItem['value'][],
    brands: VoohImpressionsFilterItem['value'][],
    cities: VoohImpressionsFilterItem['value'][],
    creations: VoohImpressionsFilterItem['value'][],
    media: VoohImpressionsFilterItem['value'][],
    network: VoohImpressionsFilterItem['value'][],
    withoutTheoretical?: boolean
  ): Promise<CAT.GetVoohImpressionsQueryData> =>
    client.query({
      query: GET_VOOH_IMPRESSIONS,
      variables: {
        campaignId,
        agglomerations,
        brands,
        cities,
        creations,
        media,
        network,
        withoutTheoretical,
      },
    }),

  getVoohImpressionsPublic: (
    voohCampaignUuid: VoohCampaign['uuid'],
    agglomerations: VoohImpressionsFilterItem['value'][],
    brands: VoohImpressionsFilterItem['value'][],
    cities: VoohImpressionsFilterItem['value'][],
    creations: VoohImpressionsFilterItem['value'][],
    media: VoohImpressionsFilterItem['value'][],
    network: VoohImpressionsFilterItem['value'][]
  ): Promise<CAT.GetVoohImpressionsPublicQueryData> =>
    client.query({
      query: GET_VOOH_IMPRESSIONS_PUBLIC,
      variables: { voohCampaignUuid, agglomerations, brands, cities, creations, media, network },
    }),

  getVoohBrandsNetworks: (term: string): Promise<CAT.GetVoohBrandsNetworksQueryData> =>
    client.query({
      query: GET_VOOH_BRANDS_NETWORK,
      variables: { term },
    }),

  getVoohCampaign: (id: VoohCampaign['id']): Promise<CAT.GetVoohCampaignAQR> =>
    client.query({
      query: GET_VOOH_CAMPAIGN,
      variables: { id },
    }),

  getVoohCampaignPublicReport: (
    voohCampaignUuid: VoohCampaign['uuid']
  ): Promise<CAT.GetVoohCampaignPublicReportAQR> =>
    client.query({
      query: GET_VOOH_CAMPAIGN_PUBLIC,
      variables: { voohCampaignUuid },
    }),

  destroyVoohCampaign: (
    id: VoohCampaign['id']
  ): Promise<FetchResult<{ destroyVoohCampaign: MutationStateResponse }>> =>
    client.mutate({
      mutation: DESTROY_VOOH_CAMPAIGN,
      variables: { id },
    }),

  stopVoohCampaign: (
    id: VoohCampaign['id']
  ): Promise<FetchResult<{ stopVoohCampaign: MutationStateResponse }>> =>
    client.mutate({
      mutation: STOP_VOOH_CAMPAIGN,
      variables: { id },
    }),

  copyVoohCampaign: (
    id: VoohCampaign['id']
  ): Promise<FetchResult<{ copyVoohCampaign: MutationStateResponse }>> =>
    client.mutate({
      mutation: COPY_VOOH_CAMPAIGN,
      variables: { id },
    }),

  createVoohCampaignSummaryPdf: (
    voohCampaignUuid: VoohCampaign['uuid']
  ): Promise<
    FetchResult<{
      createVoohCampaignSummaryPdf: {
        task: FetchPdfTaskStatusResponse
      } & MutationStateResponse
    }>
  > =>
    client.mutate({
      mutation: CREATE_VOOH_CAMPAIGN_SUMMARY_PDF,
      variables: { voohCampaignUuid },
    }),

  publishVoohCampaign: (
    id: VoohCampaign['id']
  ): Promise<FetchResult<{ publishVoohCampaign: MutationStateResponse }>> =>
    client.mutate({
      mutation: PUBLISH_VOOH_CAMPAIGN,
      variables: { id },
    }),

  createVoohMediaTemplate: (
    name: string,
    mediaIds: VoohMedia['id'][]
  ): Promise<CAT.CreateVoohMediaTemplateFR> =>
    client.mutate({
      mutation: CREATE_VOOH_MEDIA_TEMPLATE,
      variables: { name, mediaIds },
    }),

  getVoohMediaTemplates: (
    mediaFormat: VoohMediaFormat
  ): Promise<CAT.GetVoohMediaTemplatesQueryData> =>
    client.query({
      query: GET_VOOH_MEDIA_TEMPLATES,
      variables: { mediaFormat },
    }),

  removeVoohMediaTemplate: (
    id: VoohMediaTemplate['id']
  ): Promise<FetchResult<{ removeVoohMediaTemplate: MutationStateResponse }>> =>
    client.mutate({
      mutation: REMOVE_VOOH_MEDIA_TEMPLATE,
      variables: { id },
    }),

  fetchVoohCampaignLocationsFile: (
    voohCampaignId: VoohCampaign['id']
  ): Promise<VoohCampaignLocationsFileResponse> => {
    return client.query({
      query: GET_VOOH_CAMPAIGN_LOCATION_FILE,
      variables: { voohCampaignId },
    })
  },

  getVoohCampaignEmissions: (
    voohCampaignId: VoohCampaign['id']
  ): Promise<CAT.GetVoohCampaignEmissionsAQR> =>
    client.query({
      query: GET_VOOH_CAMPAIGN_EMISSIONS,
      variables: { voohCampaignId },
    }),

  fetchVoohCampaignLocationsFilePublic: (
    voohCampaignUuid: VoohCampaign['uuid']
  ): Promise<VoohCampaignLocationsFilePublicResponse> => {
    return client.query({
      query: GET_VOOH_CAMPAIGN_LOCATION_FILE_PUBLIC,
      variables: { voohCampaignUuid },
    })
  },
}

export default voohCampaignApi
